import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  EventHubApiService,
  EventHubWebhookDeliveryListItem,
  EventHubWebhookSubscriptionDetail
} from './event-hub-api.service';

@Component({
  standalone: true,
  selector: 'ain-event-hub-webhook-subscription-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinDatePipe,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    ConfirmDialogModule,
    RouterLink
  ],
  providers: [ConfirmationService],
  template: `
    <ain-page
      [title]="detail()?.name ?? t('eventHub.webhooks.detail.title')"
      [description]="t('eventHub.webhooks.detail.description')">
      <div page-actions>
        <a [routerLink]="['/event-hub/webhook-subscriptions']">{{ t('eventHub.webhooks.backToList') }}</a>
        <button pButton type="button" severity="warn" [label]="t('eventHub.webhooks.rotateSecret')" (click)="confirmRotate()"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (detail()) {
        <section class="card">
          <div class="grid">
            <div><strong>{{ t('eventHub.catalog.fields.eventType') }}:</strong> {{ detail()!.eventType }}</div>
            <div><strong>{{ t('eventHub.webhooks.fields.endpoint') }}:</strong> {{ detail()!.endpointUrl }}</div>
            <div><strong>{{ t('eventHub.webhooks.fields.secret') }}:</strong> {{ detail()!.signingSecretMasked }}</div>
            <div><strong>{{ t('eventHub.webhooks.fields.keyId') }}:</strong> {{ detail()!.signatureKeyId }}</div>
          </div>
          <label>
            <span>{{ t('eventHub.webhooks.fields.allowedDomains') }}</span>
            <input pInputText [(ngModel)]="allowedDomainsText" />
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="deliverPii" [binary]="true" />
            <span>{{ t('eventHub.webhooks.fields.deliverPii') }}</span>
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="isActive" [binary]="true" />
            <span>{{ t('eventHub.webhooks.active') }}</span>
          </label>
        </section>

        @if (rotatedSecret()) {
          <p class="secret-banner">{{ t('eventHub.webhooks.rotate.result') }}: <code>{{ rotatedSecret() }}</code></p>
        }

        <h3>{{ t('eventHub.webhooks.deliveries.title') }}</h3>
        @if (!deliveries().length) {
          <ain-state type="empty" [title]="t('eventHub.webhooks.deliveries.empty')" />
        } @else {
          <p-table [value]="deliveries()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('eventHub.catalog.fields.eventType') }}</th>
                <th>{{ t('eventHub.webhooks.deliveries.attempt') }}</th>
                <th>HTTP</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('eventHub.webhooks.deliveries.deliveredAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.eventType }}</td>
                <td>{{ row.attempt }}</td>
                <td>{{ row.statusCode ?? '-' }}</td>
                <td>{{ row.isSuccess ? t('eventHub.webhooks.deliveries.success') : t('eventHub.webhooks.deliveries.failed') }}</td>
                <td>{{ row.deliveredAtUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      }
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .card { display: grid; gap: 10px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; gap: 6px; }
    label { display: grid; gap: 6px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .secret-banner { background: #fff7ed; border: 1px solid #fdba74; border-radius: 8px; padding: 10px; color: #9a3412; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; margin-inline-end: 8px; }
  `]
})
export class EventHubWebhookSubscriptionDetailComponent implements OnInit {
  private readonly api = inject(EventHubApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  readonly detail = signal<EventHubWebhookSubscriptionDetail | null>(null);
  readonly deliveries = signal<EventHubWebhookDeliveryListItem[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly rotatedSecret = signal<string | null>(null);

  allowedDomainsText = '';
  deliverPii = false;
  isActive = true;
  private subscriptionId = '';

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.subscriptionId = params.get('id') ?? '';
      if (!this.subscriptionId) {
        this.error.set(this.t('eventHub.webhooks.detail.invalidRoute'));
        this.loading.set(false);
        return;
      }
      this.load(this.subscriptionId);
    });
  }

  load(id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getWebhookSubscription(id).subscribe({
      next: item => {
        this.detail.set(item);
        this.allowedDomainsText = item.allowedDomains.join(', ');
        this.deliverPii = item.deliverPii;
        this.isActive = item.isActive;
        this.loading.set(false);
        this.loadDeliveries(id);
      },
      error: () => {
        this.detail.set(null);
        this.error.set(this.t('eventHub.webhooks.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  loadDeliveries(id: string): void {
    this.api.listWebhookDeliveries(id).subscribe({
      next: rows => this.deliveries.set(rows),
      error: () => this.deliveries.set([])
    });
  }

  save(): void {
    const current = this.detail();
    if (!current) return;
    this.api.updateWebhookSubscription(this.subscriptionId, {
      name: current.name,
      endpointUrl: current.endpointUrl,
      allowedDomains: this.allowedDomainsText.split(',').map(item => item.trim()).filter(Boolean),
      maxDeliveriesPerMinute: current.maxDeliveriesPerMinute,
      deliverPii: this.deliverPii,
      isActive: this.isActive,
      maxAttempts: current.maxAttempts
    }).subscribe({
      next: updated => {
        this.detail.set(updated);
        this.error.set(null);
      },
      error: () => this.error.set(this.t('eventHub.webhooks.errorUpdate'))
    });
  }

  confirmRotate(): void {
    this.confirmation.confirm({
      header: this.t('eventHub.webhooks.rotate.confirmTitle'),
      message: this.t('eventHub.webhooks.rotate.confirmMessage'),
      accept: () => this.rotateSecret()
    });
  }

  rotateSecret(): void {
    this.api.rotateWebhookSecret(this.subscriptionId).subscribe({
      next: result => {
        this.rotatedSecret.set(result.newSigningSecret);
        this.load(this.subscriptionId);
      },
      error: () => this.error.set(this.t('eventHub.webhooks.errorRotate'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
